import { Directive, HostBinding, Inject, Input, OnChanges, OnDestroy, OnInit, Optional } from '@angular/core'
import { Subject, combineLatest, distinctUntilChanged, switchMap, takeUntil } from 'rxjs'
import { FeatureFlagKeyOfType } from '../feature-flags'
import { LaunchDarklyService } from '../services'
import { FEATURE_FLAG_OVERRIDES, FeatureFlagOverrides } from '../services/feature-flag-overrides'

@Directive({
  selector: '[applyClassIfFeatureFlagEnabled]',
  standalone: false,
})
export class ApplyClassIfFeatureFlagEnabledDirective implements OnInit, OnChanges, OnDestroy {
  @Input() applyClassIfFeatureFlagEnabled: FeatureFlagKeyOfType<boolean>

  @Input() featureFlaggedClassName: string | undefined

  @HostBinding('class') appliedClass: string | undefined = undefined

  readonly finalize = new Subject<void>()
  readonly flagChanged = new Subject<FeatureFlagKeyOfType<boolean>>()
  readonly classNameChanged = new Subject<string | undefined>()

  constructor(
    private ldService: LaunchDarklyService,
    @Optional() @Inject(FEATURE_FLAG_OVERRIDES) public overrides: FeatureFlagOverrides | undefined,
  ) {}

  ngOnInit(): void {
    if (this.overrides && this.applyClassIfFeatureFlagEnabled) {
      const flagEnabled = this.overrides.allFlags[this.applyClassIfFeatureFlagEnabled]
      this.updateClassName(flagEnabled)
    } else {
      combineLatest([this.flagChanged.pipe(distinctUntilChanged()), this.classNameChanged.pipe(distinctUntilChanged())])
        .pipe(
          switchMap(([key]) => this.ldService.observeFlagChanges<boolean>(key)),
          takeUntil(this.finalize),
        )
        .subscribe(enabled => this.updateClassName(enabled))
      this.flagChanged.next(this.applyClassIfFeatureFlagEnabled)
      this.classNameChanged.next(this.featureFlaggedClassName)
    }
  }

  ngOnChanges(): void {
    this.flagChanged.next(this.applyClassIfFeatureFlagEnabled)
    this.classNameChanged.next(this.featureFlaggedClassName)
  }

  ngOnDestroy(): void {
    this.finalize.next()
    this.finalize.complete()
  }

  private updateClassName(flagEnabled: boolean): void {
    this.appliedClass = flagEnabled ? this.featureFlaggedClassName : undefined
  }
}
